// src/pages/BlogPost.jsx
import { useMemo, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { POSTS } from "../blog/posts";
import { Calendar, Clock, Share2, ArrowLeft, ArrowRight, Tag as TagIcon, Home, CheckCircle2 } from "lucide-react";
import Container from "../components/ui/Container";
import Badge from "../components/ui/Badge";

function formatDate(d) {
    try {
        return new Date(d).toLocaleDateString("de-DE", { year: "numeric", month: "long", day: "2-digit" });
    } catch { return d || ""; }
}

export default function BlogPost() {
    const { slug } = useParams();

    const post = useMemo(() => POSTS.find((p) => p.slug === slug), [slug]);

    useEffect(() => {
        if (post?.title) {
            document.title = `${post.title} | Fallenjagd Blog | Tuscher Helmut`;
            
            // Meta Description
            const metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc && post.excerpt) {
                metaDesc.setAttribute("content", post.excerpt);
            }
            
            // Canonical URL
            let canonical = document.querySelector('link[rel="canonical"]');
            if (!canonical) {
                canonical = document.createElement('link');
                canonical.rel = 'canonical';
                document.head.appendChild(canonical);
            }
            canonical.href = `https://tuscherhelmut-betonrohrfallen.de/blog/${post.slug}`;
            
            // Schema.org Article
            const articleSchema = {
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": post.title,
                "description": post.excerpt,
                "image": post.cover ? `https://tuscherhelmut-betonrohrfallen.de${post.cover}` : undefined,
                "datePublished": post.date,
                "dateModified": post.date,
                "author": {
                    "@type": "Person",
                    "name": "Helmut Tuscher",
                    "url": "https://tuscherhelmut-betonrohrfallen.de/ueber"
                },
                "publisher": {
                    "@type": "Organization",
                    "name": "Tuscher Helmut Betonrohrfallen",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://tuscherhelmut-betonrohrfallen.de/images/logo.webp"
                    }
                },
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": `https://tuscherhelmut-betonrohrfallen.de/blog/${post.slug}`
                }
            };
            
            const script = document.createElement("script");
            script.id = "article-schema";
            script.type = "application/ld+json";
            script.textContent = JSON.stringify(articleSchema);
            
            const existing = document.getElementById("article-schema");
            if (existing) existing.remove();
            document.head.appendChild(script);
        }
        
        window.scrollTo(0, 0);
        
        return () => {
            const el = document.getElementById("article-schema");
            if (el) el.remove();
        };
    }, [post]);

    if (!post) {
        return (
            <Container className="py-20 text-center">
                <h1 className="text-2xl font-bold">Beitrag nicht gefunden</h1>
                <Link to="/blog" className="mt-4 inline-block text-brand-600 font-bold uppercase tracking-widest text-xs">Zurück zur Übersicht</Link>
            </Container>
        );
    }

    const handleShare = async () => {
        const shareData = {
            title: post.title,
            text: post.excerpt,
            url: window.location.href,
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(window.location.href);
                alert("Link wurde in die Zwischenablage kopiert! ✅");
            }
        } catch (err) {
            console.error("Fehler beim Teilen:", err);
        }
    };

    return (
        <div className="relative overflow-hidden min-h-screen">
            {/* Background Glows */}
            <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute top-0 right-0 h-[40%] w-[40%] rounded-full bg-brand-500/5 blur-[120px]" />
                <div className="absolute bottom-10 left-10 h-[30%] w-[30%] rounded-full bg-blue-500/5 blur-[100px]" />
            </div>

            {/* Post Hero Section */}
            <div className="relative pt-10 pb-16 md:pt-16 md:pb-24 overflow-hidden">
                <Container>
                    <div className="max-w-4xl mx-auto">
                        {/* Navigation Row */}
                        <div className="flex items-center justify-between mb-10">
                            <Link to="/blog" className="group flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-brand-600 transition-colors">
                                <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
                                Zurück zum Blog
                            </Link>
                            <Link to="/" className="text-slate-300 hover:text-slate-900 transition-colors">
                                <Home className="h-4 w-4" />
                            </Link>
                        </div>

                        {/* Title & Metadata */}
                        <div className="space-y-6 mb-12">
                            <div className="flex flex-wrap items-center gap-3">
                                {post.tags?.map(t => (
                                    <Badge key={t} className="bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 border-none uppercase tracking-widest text-[9px] font-black">{t}</Badge>
                                ))}
                            </div>
                            <h1 className="text-4xl sm:text-6xl font-heading font-black tracking-tight text-slate-900 dark:text-white leading-[1.1] uppercase">
                                {post.title}
                            </h1>
                            <div className="flex flex-wrap items-center gap-6 pt-4 text-xs font-bold text-slate-400 uppercase tracking-widest">
                                <div className="flex items-center gap-2">
                                    <Calendar className="h-3.5 w-3.5 text-brand-600" />
                                    {formatDate(post.date)}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Clock className="h-3.5 w-3.5 text-brand-600" />
                                    5 Min. Lesezeit
                                </div>
                                <div className="flex items-center gap-2 text-slate-900 dark:text-white">
                                    <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                                    Praxiswissen
                                </div>
                            </div>
                        </div>

                        {/* Featured Image */}
                        {post.cover && (
                            <div className="relative rounded-[3rem] overflow-hidden border border-slate-200 dark:border-slate-800 shadow-2xl group">
                                <img src={post.cover} alt={`${post.title} - Titelbild`} className="w-full h-auto object-cover max-h-[500px]" />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent pointer-events-none" />
                            </div>
                        )}
                    </div>
                </Container>
            </div>

            {/* Content Section */}
            <article className="pb-24">
                <Container>
                    <div className="max-w-3xl mx-auto">
                        <div className="prose prose-lg dark:prose-invert prose-brand max-w-none 
                            prose-headings:font-heading prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tight
                            prose-p:text-slate-600 dark:prose-p:text-slate-400 prose-p:leading-relaxed
                            prose-strong:text-slate-900 dark:prose-strong:text-white prose-strong:font-bold
                            prose-img:rounded-[2rem] prose-img:shadow-xl prose-img:border prose-img:border-slate-100 dark:prose-img:border-slate-800
                        ">
                            {post.video && (
                                <div className="mb-10 aspect-video rounded-[2rem] overflow-hidden border border-slate-200 dark:border-slate-800 shadow-xl bg-slate-900">
                                    <iframe
                                        src={post.video}
                                        className="w-full h-full"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </div>
                            )}
                            <div dangerouslySetInnerHTML={{ __html: post.content || post.excerpt }} />
                        </div>

                        {/* Footer Info Box */}
                        <div className="mt-20 p-8 sm:p-12 rounded-[3rem] bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 flex flex-col items-center text-center group transition-all hover:bg-white dark:hover:bg-slate-950">
                            <div className="h-12 w-12 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-600 mb-6">
                                <CheckCircle2 className="h-6 w-6" />
                            </div>
                            <h3 className="text-xl font-heading font-black text-slate-900 dark:text-white uppercase mb-4">Expertenwissen teilen</h3>
                            <p className="text-slate-500 max-w-md mx-auto mb-8">
                                Hat dir dieser Beitrag geholfen? Dann teile ihn doch mit deinen Jagdkollegen oder schau dir weitere spannende Berichte an.
                            </p>
                            <div className="flex gap-4">
                                <button 
                                    onClick={handleShare}
                                    className="flex items-center gap-2 rounded-xl bg-slate-900 dark:bg-brand-600 text-white px-5 py-3 text-[10px] font-black uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-lg"
                                >
                                    Beitrag teilen <Share2 className="h-3.5 w-3.5" />
                                </button>
                                <Link to="/blog" className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 px-5 py-3 text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 dark:hover:bg-slate-800 transition-all">
                                    Weitere Beiträge
                                </Link>
                            </div>
                        </div>

                        {/* Recommendation Banner */}
                        <div className="mt-12 rounded-[2.5rem] bg-brand-600 p-1 bg-gradient-to-tr from-brand-600 to-emerald-600 shadow-xl overflow-hidden group">
                            <div className="bg-white dark:bg-slate-950 rounded-[2.2rem] p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-8">
                                <div className="text-center md:text-left">
                                    <div className="text-[10px] font-black uppercase tracking-widest text-brand-600 mb-2">Empfehlung des Autors</div>
                                    <h4 className="text-xl sm:text-2xl font-heading font-black text-slate-900 dark:text-white uppercase leading-tight">
                                        Die perfekte Betonrohrfalle <br/> für Ihr Revier
                                    </h4>
                                </div>
                                <Link to="/shop" className="flex items-center gap-3 rounded-2xl bg-brand-600 px-6 py-4 text-white font-black uppercase tracking-widest text-xs hover:bg-brand-700 hover:shadow-xl hover:shadow-brand-500/20 active:scale-95 transition-all">
                                    Zum Shop <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </Container>
            </article>
        </div>
    );
}